import { useMemo, useRef, useState, type UIEvent } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { AUTHOR_WITH_AI_QUESTION_TYPES, isAuthorWithAiQuestionType, type QuestionOptionsPart, type QuestionSuggestionsPart } from '../../api/authorWithAiApi';
import { useInfiniteBuildQuestions } from '../../api/authorWithAiQueries';
import { FALLBACK_LIST_HEIGHT, FULL_PAGE_SIZE, LOAD_MORE_THRESHOLD_PX, PREVIEW_ROWS } from './optionsListConstants';
import { PICKER_COPY, QUESTION_TYPE_LABELS } from './authorWithAiCopy';
import { createAuthorWithAiStyles } from './authorWithAiStyles';

/**
 * A taxonomy leaf's bank questions inside the thread: the five most used,
 * then "Show all N" expanding in place into a list of the same height that
 * pages through the Question Bank filtered by the label, like the picker.
 * Rows submit only when `onPick` is given (the latest assistant message).
 */

interface Row {
  libraryId: string;
  text: string;
  questionType: string;
  usageCount: number;
  reason?: string;
}

export interface QuestionOptionsListProps {
  buildId: string;
  part: QuestionOptionsPart | QuestionSuggestionsPart;
  reasons?: boolean;
  onPick?: (libraryId: string) => void;
  disabled?: boolean;
  /** Server rejections keyed by question id without the Q prefix. */
  rowErrors?: Readonly<Record<string, string>>;
}

export function QuestionOptionsList({ buildId, part, reasons = false, onPick, disabled = false, rowErrors = {} }: QuestionOptionsListProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  const [showAll, setShowAll] = useState(false);
  const [listHeight, setListHeight] = useState<number | null>(null);
  const [atEnd, setAtEnd] = useState(false);
  const listRef = useRef<HTMLUListElement | null>(null);
  const full = useInfiniteBuildQuestions(
    buildId,
    { taxonomyLabelId: part.type === 'question_options' ? part.labelId : undefined, blockTypes: AUTHOR_WITH_AI_QUESTION_TYPES, sort: 'usedIn', order: 'desc', pageSize: FULL_PAGE_SIZE },
    { enabled: showAll },
  );

  const fullRows: Row[] | undefined = full.data?.pages.flatMap((page) => page.items).map((item) => ({
    libraryId: `Q${item.questionDefinitionId}`,
    text: item.questionTextPlain,
    questionType: item.blockType,
    usageCount: item.usageCount,
  }));
  const supportedRows = (showAll ? (fullRows ?? part.items) : part.items).filter((row) => isAuthorWithAiQuestionType(row.questionType));
  const rows: Row[] = showAll ? supportedRows : supportedRows.slice(0, PREVIEW_ROWS);
  const loadedAll = showAll && !full.hasNextPage && Boolean(full.data);
  const showCue = showAll && !(loadedAll && atEnd);

  const revealAll = () => {
    setListHeight(listRef.current?.offsetHeight || FALLBACK_LIST_HEIGHT);
    setShowAll(true);
    setAtEnd(false);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remaining < LOAD_MORE_THRESHOLD_PX && full.hasNextPage && !full.isFetchingNextPage) void full.fetchNextPage();
    setAtEnd(remaining <= 1);
  };

  return (
    <div style={styles.questionOptions} data-testid="question-options">
      <div
        style={{ ...styles.pickScroll, ...(showAll ? { height: `${listHeight ?? FALLBACK_LIST_HEIGHT}px`, overflowY: 'auto' } : {}) }}
        onScroll={showAll ? handleScroll : undefined}
        data-testid="question-options-list"
      >
        <ul ref={listRef} style={styles.pickList}>
          {rows.map((row) => {
            const error = rowErrors[row.libraryId.replace(/^Q/i, '')];
            const content = (
              <span style={{ flex: 1, minWidth: 0 }}>
                <span>{row.text}</span>
                {reasons && row.reason && <span style={styles.scenarioDescription}>{row.reason}</span>}
                <span style={styles.pickMeta}>
                  <span>{QUESTION_TYPE_LABELS[row.questionType] ?? row.questionType}</span>
                  <span>{PICKER_COPY.usedIn(row.usageCount)}</span>
                </span>
                {error && <span style={{ ...styles.error, display: 'block' }} role="alert">{error}</span>}
              </span>
            );
            return (
              <li key={row.libraryId}>
                {onPick && !reasons
                  ? <button type="button" style={styles.pickRow} onClick={() => onPick(row.libraryId)} disabled={disabled}>{content}</button>
                  : <div style={{ ...styles.pickRow, ...styles.pickRowInert }}>{content}</div>}
              </li>
            );
          })}
        </ul>
        {showCue && <div style={styles.fade} aria-hidden="true" />}
      </div>
      {showCue && <span style={styles.scrollCue}>{PICKER_COPY.scrollForMore}</span>}
      {full.isError && <p style={styles.error} role="alert">{PICKER_COPY.loadError}</p>}
      {onPick && !reasons && !showAll && part.type === 'question_options' && part.total > PREVIEW_ROWS && (
        <button type="button" style={styles.showMore} onClick={revealAll}>{PICKER_COPY.showAll(part.total)}</button>
      )}
    </div>
  );
}
